import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '../generated/api';
import { trackUserJourney, setUserContext } from '../utils/datadog';
import { getRumUser } from '../utils/users';

type UserVerificationResponse = components['schemas']['UserVerificationResponse'];
type TokenGenerationRequest = components['schemas']['TokenGenerationRequest'];
type TokenGenerationResponse = components['schemas']['TokenGenerationResponse'];
type ResetEmailRequest = components['schemas']['ResetEmailRequest'];
type ResetEmailResponse = components['schemas']['ResetEmailResponse'];

const ForgotPasswordPage: React.FC = () => {
  // Get RUM user for Product Analytics
  const username = getRumUser();
  const email = `${username}@bitsburg.io`;

  React.useEffect(() => {
    document.title = 'Bitsburg Bank - Forgot Password';

    // Track user journey step
    trackUserJourney('forgot_password_page_visited', {
      previous_page: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
    });
  }, []);

  const [formData, setFormData] = useState({
    username: username,
    email: email,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setShowErrorMessage(false);
    setShowSuccessMessage(false);

    if (!formData.username || !formData.email) {
      setErrorMessage('Please provide both username and email address.');
      setShowErrorMessage(true);
      setIsLoading(false);
      return;
    }

    try {
      // Verify username/email combination exists via API
      const verifyResponse = await fetch(
        `/api/user/verify?username=${encodeURIComponent(formData.username)}&email=${encodeURIComponent(formData.email)}`
      );

      if (!verifyResponse.ok) {
        setErrorMessage("We're experiencing temporary issues. Please try again later or contact support.");
        setShowErrorMessage(true);
        return;
      }

      const verifyResult = (await verifyResponse.json()) as UserVerificationResponse;

      // Log if there was a failure, but show success to user for security (same as Razor Pages)
      if (!verifyResult.exists) {
        setShowSuccessMessage(true);
        return;
      }

      // Generate secure reset token via Security API
      const tokenRequest: TokenGenerationRequest = {
        username: formData.username,
        email: formData.email,
      };

      const tokenResponse = await fetch('/api/security/token/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tokenRequest),
      });

      if (!tokenResponse.ok) {
        setErrorMessage("We're experiencing temporary issues. Please try again later or contact support.");
        setShowErrorMessage(true);
        return;
      }

      const tokenResult = (await tokenResponse.json()) as TokenGenerationResponse;

      if (!tokenResult.success) {
        setErrorMessage("We're experiencing temporary issues. Please try again later or contact support.");
        setShowErrorMessage(true);
        return;
      }

      // Send email with reset instructions via Email API
      const resetEmailRequest: ResetEmailRequest = {
        email: formData.email,
        username: formData.username,
        resetToken: tokenResult.token,
        expiresAt: tokenResult.expiresAt,
      };

      const emailResponse = await fetch('/api/email/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(resetEmailRequest),
      });

      if (!emailResponse.ok) {
        setErrorMessage("We're experiencing temporary issues. Please try again later or contact support.");
        setShowErrorMessage(true);
        return;
      }

      const emailResult = (await emailResponse.json()) as ResetEmailResponse;

      if (!emailResult.success) {
        setErrorMessage("We're experiencing temporary issues. Please try again later or contact support.");
        setShowErrorMessage(true);
        return;
      }

      // Success - create consistent timestamp for journey tracking
      const currentTimestamp = new Date().toISOString();

      // Set user context once for the entire RUM session
      setUserContext(formData.username, {
        password_reset_timestamp: currentTimestamp,
      });

      // Track password reset completion
      trackUserJourney('password_reset_completed', {
        timestamp: currentTimestamp,
      });

      // Success - show success message
      setShowSuccessMessage(true);
      setFormData({ username: '', email: '' });
    } catch (error) {
      window.DD_LOGS?.logger?.error('Error during password reset', { error: error });
      setErrorMessage('An unexpected error occurred. Please try again later.');
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="py-5">
      <div className="container">
        <div className="row justify-content-center align-items-center min-vh-50">
          <div className="col-lg-5 col-md-7 col-sm-9">
            <div className="bank-card bank-card-elevated bank-card-no-hover">
              <div className="bank-card-header">
                <h3 className="mb-0 fw-bold">
                  <i className="bi bi-key me-2"></i>
                  Reset Your Password
                </h3>
                <p className="mb-0 mt-2 opacity-75">We'll help you regain access to your account</p>
              </div>
              <div className="bank-card-body">
                <div className="alert bank-alert bank-alert-warning mb-4">
                  <i className="bi bi-info-circle me-2"></i>
                  For your security, please provide your username and email address associated with your account.
                </div>

                {showSuccessMessage && (
                  <div className="alert bank-alert bank-alert-success mb-4">
                    <i className="bi bi-check-circle me-2"></i>
                    Password reset instructions have been sent to your email address. Please check your inbox and follow
                    the instructions to reset your password.
                  </div>
                )}

                {showErrorMessage && (
                  <div className="alert bank-alert bank-alert-danger mb-4">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {errorMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label htmlFor="username" className="bank-form-label">
                      Username
                    </label>
                    <input
                      type="text"
                      className="form-control bank-form-control"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      placeholder="Enter your username"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="email" className="bank-form-label">
                      Email Address
                    </label>
                    <input
                      type="email"
                      className="form-control bank-form-control"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email address"
                      required
                    />
                  </div>

                  <div className="text-center">
                    <button
                      type="submit"
                      className="bank-btn-primary px-4"
                      disabled={isLoading}
                      id="forgot-password-submit-btn"
                    >
                      {!isLoading ? (
                        <span>
                          <i className="bi bi-envelope me-2"></i>
                          <span>Send Reset Instructions</span>
                        </span>
                      ) : (
                        <span>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          <span>Sending Instructions...</span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>

                <div className="text-center mt-4">
                  <Link to="/login" className="text-datadog-purple text-decoration-none">
                    <i className="bi bi-arrow-left me-1"></i>
                    Back to Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
